/**
 * Générateur — Il utilise les ordres de grandeur pour vérifier ses résultats.
 * Attendu ID : 152 | Niveau : 4ème | Domaine : Nombres (desc 11, O17)
 *
 * Source doc prof (NOMBRE ET CALCUL, cluster 3) : Ex.1 « ordre de grandeur de
 * 45×10¹² × 4×10⁻²⁶ » (notation scientifique), Créé n°7 « la calculatrice s'est-elle
 * trompée ? » (V/F plausible/impossible via ordre de grandeur). Plan validé
 * 2026-07-14 :
 *
 *   Carte « estimation »    — une calculatrice affiche un résultat, l'élève
 *                             donne l'ordre de grandeur qui le contrôle (saisie)
 *   Carte « plausible »     — résultat affiché plausible / impossible (Vrai/Faux)
 *   Carte « justification » — quel calcul d'ordre de grandeur choisir (QCM)
 *
 * Ex.2 du doc (« masse d'un atome ») écarté : donnée source erronée d'un facteur
 * 10⁷ (signalé). Arithmétique déterministe (arrondi à 1 chiffre significatif par
 * calcul entier) — gen et solveur concordent.
 *
 * Revue 2026-09-22 (audit dénombré, décisions prof) :
 *   - « plausible » : une valeur impossible finissait par 0 (×10) ou perdait
 *     le chiffre des unités (÷10) ; un élève qui ne contrôlait que ce chiffre
 *     classait neuf lignes sur dix. Les valeurs fausses gardent maintenant le
 *     chiffre des unités du produit exact (helper affichageImpossible). Au moins
 *     une ligne plausible et une impossible par tableau (12,5 % des tableaux
 *     étaient tout-plausibles ou tout-impossibles).
 *   - « justification » : l'énoncé impose l'arrondi de chaque nombre à un seul
 *     chiffre significatif (400 × 50 pour 347 × 52 était compté faux sans règle).
 *   - « scientifique » RÉÉCRITE, puis SUPPRIMÉE le 23/09 au soir (voir la revue
 *     du soir plus bas) : les deux coefficients étaient des chiffres, le
 *     produit était exact et le « ≈ » mentait (écriture scientifique d'un
 *     produit, attendu 137). Retour à l'exercice source : un coefficient à deux
 *     chiffres, arrondi à un chiffre significatif, dit dans l'énoncé.
 *   - « estimation » : recadrée sur le contrôle d'un résultat affiché par une
 *     calculatrice ; formatage local des milliers retiré (règle d'affichage
 *     globale, utils/milliers.js) ; parenthèse « sans espace » supprimée ; code
 *     mort retiré.
 *
 * Revue 2026-09-23 (typographie, règle prof) : toute expression AFFICHÉE passe
 * en KaTeX : les produits « a × b » des énoncés et des options du QCM
 * « justification », les calculs des lignes du tableau « plausible » (le carré
 * n'est plus un « ² » Unicode). Helper `exp` de nombres.js abandonné ici. Les résultats affichés par la
 * calculatrice restent des nombres en texte (milliers espacés par la règle
 * globale) ; les réponses tapées ne changent pas.
 *
 * Revue 2026-09-23 au soir (décision prof) : la carte « scientifique » (libellé
 * « Ordre de grandeur en notation scientifique », scientifique_texte_trou_saisie)
 * est SUPPRIMÉE. Motif donné : elle n'est pas adaptée au programme de 4e en
 * vigueur, et l'attendu a déjà assez de cartes pour ne pas en avoir besoin. Les
 * trois qui restent couvrent l'intitulé (estimer, juger un affichage, choisir le
 * calcul de contrôle), et « justification » garde le QCM que tout attendu doit
 * avoir pour rester dépouillable en mode classe par QR code.
 */

import { randomChoice, randInt, shuffle, tex, puissTex, makeSaisie, makeVraiFaux, makeQcm } from '../../../utils';

const ATTENDU_ID = 152;

const L_ESTIMATION = 'Estimer l\'ordre de grandeur d\'un produit';
const L_PLAUSIBLE = 'Juger la plausibilité d\'un résultat affiché';
const L_JUSTIFICATION = 'Choisir un calcul d\'ordre de grandeur';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Arrondi à UN seul chiffre significatif (entier, déterministe).
const round1sig = (x) => { const p = 10 ** (String(Math.abs(x)).length - 1); return Math.round(x / p) * p; };

// Produit affiché « $a \times b$ » (énoncés, options du QCM, lignes du tableau).
const produitTex = (x, y) => tex(`${x} \\times ${y}`);

// Résultat impossible d'une calculatrice : environ dix fois trop grand ou dix
// fois trop petit, MAIS avec le chiffre des unités du produit exact, pour qu'un
// contrôle du seul chiffre des unités ne suffise jamais. Le chiffre des dizaines
// inséré dans la version « trop grand » est tiré au hasard : aucun motif à
// repérer (un 0 systématique en serait un).
const affichageImpossible = (exact) => {
    const unites = exact % 10;
    return randomChoice([true, false])
        ? Math.floor(exact / 10) * 100 + randInt(0, 9) * 10 + unites   // ≈ 10 × exact
        : Math.floor(exact / 100) * 10 + unites;                         // ≈ exact ÷ 10
};

// ═════════ Carte « estimation » — contrôler un résultat affiché (saisie) ═════════

const genEstimation = () => {
    const a = randInt(11, 899);
    const b = randInt(11, 899);
    const exact = a * b;
    // Le résultat affiché est juste une fois sur deux ; la réponse attendue est
    // l'ordre de grandeur, la même dans les deux cas.
    const juste = randomChoice([true, false]);
    const affiche = juste ? exact : affichageImpossible(exact);
    const rep = round1sig(a) * round1sig(b);
    const payload = makeSaisie(String(rep),
        `<p>Une calculatrice affiche <b>${affiche}</b> pour le produit <b>${produitTex(a, b)}</b>.</p>`
        + '<p><b>Pour contrôler ce résultat, arrondis chaque nombre à un seul chiffre significatif '
        + 'et donne l\'ordre de grandeur du produit.</b></p>',
        opts(L_ESTIMATION));
    payload._meta.params = { a, b, affiche, juste, rep };
    return payload;
};

// ═════════ Carte « plausible » — calculatrice trompée ? (tableau) ═════════

const genPlausible = () => {
    const lignes = [];
    const bonnes = [];
    // Au moins une ligne plausible ET une impossible (jamais de tableau tout-vrai
    // ni tout-faux) : deux fixées, deux tirées, l'ordre mélangé.
    const plausibles = shuffle([true, false, randomChoice([true, false]), randomChoice([true, false])]);
    let secu = 0;
    while (lignes.length < 4 && secu++ < 100) {
        const type = randomChoice(['produit', 'carre']);
        let exact; let enonce;
        if (type === 'produit') {
            const a = randInt(11, 99); const b = randInt(11, 99);
            exact = a * b; enonce = produitTex(a, b);
        } else {
            const n = randInt(11, 99); exact = n * n; enonce = puissTex(n, 2);
        }
        if (lignes.some(l => l.startsWith(`${enonce} →`))) continue;     // pas deux fois le même calcul
        const plausible = plausibles[lignes.length];
        lignes.push(`${enonce} → affiché : ${plausible ? exact : affichageImpossible(exact)}`);
        bonnes.push(plausible ? 0 : 1);
    }
    const ordre = shuffle(lignes.map((_, i) => i));
    const L = ordre.map(i => lignes[i]);
    const B = ordre.map(i => bonnes[i]);
    const payload = makeVraiFaux(L, B,
        '<p>Un élève a tapé chaque calcul à la calculatrice. Sans refaire le calcul exact, '
        + 'utilise un <b>ordre de grandeur</b> pour juger chaque résultat affiché.</p>',
        { ...opts(L_PLAUSIBLE), colonnes: ['Plausible', 'Impossible'] });
    payload._meta.params = { lignes: L, bonnes: B };
    return payload;
};

// ═════════ Carte « justification » — QCM ═════════

const genJustification = () => {
    const a = randInt(11, 899);
    const b = randInt(11, 899);
    const r1 = round1sig(a);
    const r2 = round1sig(b);
    const p1 = 10 ** (String(a).length - 1);
    const p2 = 10 ** (String(b).length - 1);
    const correct = produitTex(r1, r2);
    const choix = new Set([correct]);
    [produitTex(r1 + p1, r2), produitTex(r1, r2 + p2), produitTex(Math.max(r1 - p1, p1), r2 + p2), produitTex(a, r2)]
        .forEach(c => { if (choix.size < 4) choix.add(c); });
    const payload = makeQcm([...choix], correct,
        `<p>Pour vérifier le résultat de <b>${produitTex(a, b)}</b>, on arrondit chaque nombre à un seul chiffre significatif.</p>`
        + '<p><b>Quel calcul d\'ordre de grandeur peux-tu alors faire de tête ?</b></p>',
        opts(L_JUSTIFICATION));
    payload._meta.params = { a, b, correct: `${r1} × ${r2}` };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    estimation_texte: genEstimation,
    plausible_tableau_vf: genPlausible,
    justification_qcm_unique: genJustification,
};

const FORMAT_LABELS = { texte: 'Saisie', tableau_vf: 'Vrai/Faux', qcm_unique: 'QCM', texte_trou_saisie: 'Saisie trous' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'estimation', label: L_ESTIMATION, formatsList: buildFormats('estimation') },
    { id: 'plausible', label: L_PLAUSIBLE, formatsList: buildFormats('plausible') },
    { id: 'justification', label: L_JUSTIFICATION, formatsList: buildFormats('justification') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
